import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

// Shared chrome for the Home dashboard's boxed sections (Up next, Deadlines,
// Pipeline): a title row with an optional right-hand link or caption, above a
// single boxed card. Keeping it in one place is what keeps the columns aligned.

/** Row treatment for a clickable row inside a `BoxedList`. */
export const BOXED_ROW_CLASS =
  "px-4 py-2.5 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"

/**
 * Layout for a work row (status label · title and meta · aside · action).
 * From `sm` up it is one line. On a phone the fixed label column, the aside
 * and the action left the title a letter or two wide, so below `sm` the row
 * wraps at a ROW_BREAK_CLASS element: label and aside on the first line, the
 * title and the action on the second (the row orders its items with
 * `max-sm:order-*` where the source order differs).
 */
export const ROW_LAYOUT_CLASS = "group flex flex-wrap items-center gap-x-3 gap-y-1 sm:flex-nowrap"

/** An empty, full-width flex item that breaks a work row's line on phones; gone from `sm` up. */
export const ROW_BREAK_CLASS = "basis-full sm:hidden"

/** Right-hand header link ("View all …", "Open Bids"). */
export const SECTION_LINK_CLASS =
  "inline-flex items-center gap-0.5 text-[13px] text-[hsl(var(--link))] hover:underline"

export function SectionHeader({ title, aside }: { title: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <h2 className="text-sm font-medium">{title}</h2>
      {aside}
    </div>
  )
}

export function BoxedList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      data-slot="boxed-list"
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-card shadow-sm divide-y divide-border",
        className,
      )}
    >
      {children}
    </div>
  )
}

/** Empty state that sits inside the box, so a column never leaves a blank gap. */
export function BoxedListEmpty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">{children}</p>
}
